import omitDeepLodash from "omit-deep-lodash";
import { MutationTree, GetterTree, ActionTree } from "vuex";
import {} from "vuefront-api";
import { RootState } from "vuefront-store";
import {
  getGraphQLErrors,
  shouldInvalidateApolloCache,
} from "./apollo.helpers";

export type State = {
  data: Record<string, unknown>;
  prefetchData: Record<string, unknown>;
  keyedData: Record<string, unknown>;
};
export const state = (): State => ({
  data: {},
  prefetchData: {},
  keyedData: {},
});

export const getters: GetterTree<State, RootState> = {
  get(state) {
    return state.data;
  },
  prefetchData(state) {
    return state.prefetchData;
  },
  getByKey: (state) => (key: string) => state.keyedData[key],
};

/**
 * Typed accessor for the legacy shared response bucket. Callers that know
 * the operation's response shape can read it without `any`:
 *
 *   const payload = getMutationResult<AddToCartData["addToCart"]>(
 *     rootGetters, "addToCart"
 *   );
 */
export const getMutationResult = <T>(
  rootGetters: any,
  field: string
): T | undefined => {
  const data = rootGetters["apollo/get"] as Record<string, unknown>;
  return data?.[field] as T | undefined;
};

export const mutations: MutationTree<State> = {
  setData(state, payload) {
    state.data = { ...state.data, ...payload.data };
  },
  setPrefetchData(state, { key, data }) {
    state.prefetchData[key] = data;
  },
  setKeyedData(state, { key, data }) {
    state.keyedData = { ...state.keyedData, [key]: data };
  },
};

export const actions: ActionTree<State, RootState> = {
  async query({ commit }, options) {
    const throwOnError = options?.throwOnError === true;
    try {
      // `key` is consumed here only — it must not be forwarded to Apollo.
      const {
        key,
        invalidateCache: _invalidateCache,
        throwOnError: _throwOnError,
        ...apolloOptions
      } = options || {};
      const variables = omitDeepLodash(apolloOptions.variables, "__typename");
      let res = await this.$vfapollo.query({
        ...apolloOptions,
        variables,
        errorPolicy: "all",
      });

      res = omitDeepLodash(res, "__typename");
      const errors = getGraphQLErrors(res);

      if (errors) {
        commit("vuefront/setError", errors, { root: true });
        if (throwOnError) {
          const graphQLErrors = Array.isArray(errors) ? errors : [errors];
          const error: any = new Error(
            graphQLErrors
              .map((item: any) => String(item?.message || item || ""))
              .filter(Boolean)
              .join("; ") || "GraphQL query failed"
          );
          error.graphQLErrors = graphQLErrors;
          throw error;
        }
        return null;
      }

      commit("vuefront/setError", false, { root: true });

      // Always keep the legacy shared `data` object working for the ~25
      // components that still read `apollo/get`.
      commit("setData", res);

      const data = res?.data ?? {};
      // Optional keyed storage: lets a caller read its own result back via
      // `apollo/getByKey` without it being clobbered by a concurrent query.
      if (key) {
        commit("setKeyedData", { key, data });
      }

      // Return the result so callers can use their OWN data instead of the
      // shared getter — this is what removes the concurrent-query race that
      // the catalog/product pages previously guarded against by hand.
      return data as Record<string, unknown>;
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
      if (throwOnError) throw e;
      return null;
    }
  },
  async mutate({ commit }, options) {
    try {
      const { invalidateCache, ...mutationOptions } = options || {};
      const variables = omitDeepLodash(mutationOptions.variables, "__typename");
      const res = await this.$vfapollo.mutate({
        ...mutationOptions,
        variables,
        errorPolicy: "all",
      });
      const errors = getGraphQLErrors(res);

      if (errors) {
        commit("vuefront/setError", errors, {
          root: true,
        });
        return null;
      }

      commit("vuefront/setError", false, { root: true });
      commit("setData", res);

      if (shouldInvalidateApolloCache({ invalidateCache })) {
        await this.$vfapollo.clearStore();
      }

      return res?.data ?? null;
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
      return null;
    }
  },
  async upload(
    { commit },
    options: { variables: { [key: string]: any }; mutation: any }
  ) {
    try {
      const variables: { [key: string]: any } = omitDeepLodash(
        options.variables,
        "__typename"
      );

      const o = {
        query: options.mutation.loc.source.body,
        variables,
      };

      const map: { [key: number]: string } = {
        0: "file",
      };

      let i: number = 0;
      const fd = new FormData();
      fd.append("operations", JSON.stringify(o));
      for (const key in variables) {
        if (
          typeof variables[key] === "object" &&
          variables[key] instanceof File
        ) {
          map[i] = key;
          fd.append(i.toString(), variables[key]);
          i++;
        }
      }

      fd.append("map", JSON.stringify(map));
      // Only file uploads use axios; keep it out of the entry chunk.
      //@ts-ignore
      const { axios: adapterAxios } = await import("@bundled-es-modules/axios");
      const axios = adapterAxios.create({
        withCredentials: true,
      });
      const { data } = await axios.post(this.$vuefront.baseURL, fd);

      if (!data.errors) {
        commit("vuefront/setError", false, { root: true });
        commit("setData", data);
      } else {
        commit("vuefront/setError", data.errors, {
          root: true,
        });
      }
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
    }
  },
};
